import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Input, message, Modal, Popconfirm, Table, Tag } from "antd";
import { DeleteOutlined, UserAddOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import service from "../../api/FetchInterceptor";
import { formatMoney, PROVIDER_LABELS } from "../../lib/money";

const FormationStudents = ({ formationId, onChange }) => {
    const [form] = Form.useForm();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        service({ method: "GET", url: `admin/formations/${formationId}/enrollments` })
            .then(setRows)
            .finally(() => setLoading(false));
    }, [formationId]);

    useEffect(() => {
        load();
    }, [load]);

    const changed = () => {
        load();
        onChange?.();
    };

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) => `${r.user?.name} ${r.user?.email} ${r.user?.phone ?? ""}`.toLowerCase().includes(q));
    }, [rows, search]);

    const grant = (values) => {
        setSaving(true);
        service({ method: "POST", url: `admin/formations/${formationId}/enrollments`, data: values })
            .then((data) => {
                message.success(
                    data.created_account
                        ? "Compte créé et accès donné. L'apprenante pourra définir son mot de passe plus tard."
                        : "Accès donné"
                );
                setModalOpen(false);
                form.resetFields();
                changed();
            })
            .catch((e) => {
                const errors = e?.response?.data?.errors;
                message.error(errors ? Object.values(errors).flat()[0] : "Erreur lors de l'enregistrement");
            })
            .finally(() => setSaving(false));
    };

    const revoke = (row) => {
        service({ method: "DELETE", url: `admin/formations/${formationId}/enrollments/${row.id}` })
            .then(() => {
                message.success("Accès retiré");
                changed();
            })
            .catch((e) => message.error(e?.response?.data?.message ?? "Erreur lors du retrait"));
    };

    const columns = [
        {
            key: "user",
            title: "Apprenante",
            render: (_, r) => (
                <div>
                    <div className="font-medium">{r.user?.name}</div>
                    <div className="text-xs text-gray-500">{r.user?.email}</div>
                </div>
            ),
        },
        {
            key: "phone",
            title: "Téléphone",
            render: (_, r) => r.user?.phone ?? <span className="text-gray-400">—</span>,
        },
        {
            key: "source",
            dataIndex: "source",
            title: "Accès",
            render: (source, r) =>
                source === "purchase" ? (
                    <Tag color="green">
                        Achat · {formatMoney(r.order.amount_major, r.order.currency)} ·{" "}
                        {PROVIDER_LABELS[r.order.provider] ?? r.order.provider}
                    </Tag>
                ) : (
                    <Tag color="purple">Accès manuel</Tag>
                ),
        },
        {
            key: "granted_at",
            dataIndex: "granted_at",
            title: "Depuis le",
            render: (d) => dayjs(d).format("DD/MM/YYYY à HH:mm"),
        },
        {
            key: "actions",
            width: 60,
            render: (_, r) =>
                r.source === "manual" ? (
                    <Popconfirm
                        title="Retirer l'accès à cette formation ?"
                        description="Le compte de l'apprenante est conservé."
                        okText="Retirer"
                        cancelText="Annuler"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => revoke(r)}
                    >
                        <Button type="text" danger icon={<DeleteOutlined />} title="Retirer l'accès" />
                    </Popconfirm>
                ) : null,
        },
    ];

    return (
        <>
            <Modal
                title="Donner un accès"
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onOk={() => form.submit()}
                okText="Donner l'accès"
                cancelText="Annuler"
                confirmLoading={saving}
                destroyOnHidden
            >
                <Alert
                    className="mb-4"
                    type="info"
                    showIcon
                    message="Si l'e-mail n'a pas encore de compte, il est créé automatiquement."
                />
                <Form form={form} layout="vertical" onFinish={grant} preserve={false}>
                    <Form.Item
                        name="email"
                        label="E-mail"
                        rules={[
                            { required: true, message: "Indiquez un e-mail" },
                            { type: "email", message: "E-mail invalide" },
                        ]}
                    >
                        <Input placeholder="apprenante@exemple.com" />
                    </Form.Item>
                    <Form.Item name="name" label="Nom" extra="Utilisé seulement si le compte doit être créé.">
                        <Input placeholder="Prénom Nom" />
                    </Form.Item>
                </Form>
            </Modal>

            <div className="mb-4 flex items-center justify-between gap-4">
                <Input.Search
                    allowClear
                    placeholder="Rechercher par nom, e-mail ou téléphone"
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ maxWidth: 380 }}
                />
                <Button type="primary" icon={<UserAddOutlined />} onClick={() => setModalOpen(true)}>
                    Donner un accès
                </Button>
            </div>

            <Table
                rowKey="id"
                loading={loading}
                columns={columns}
                dataSource={filtered}
                pagination={{ pageSize: 25, hideOnSinglePage: true }}
                locale={{ emptyText: "Aucune apprenante pour l'instant" }}
            />
        </>
    );
};

export default FormationStudents;
